import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { PlayerProvider, usePlayer } from "@/context/PlayerContext";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MiniPlayer } from "@/components/MiniPlayer";
import { LicenseModal } from "@/components/LicenseModal";
import { FreeUnlockModal } from "@/components/FreeUnlockModal";
import { PromoBanner } from "@/components/PromoBanner";
import { ChatWidget } from "@/components/ChatWidget";
import Home from "@/pages/Home";
import Beats from "@/pages/Beats";
import Catalog from "@/pages/Catalog";
import Showcase from "@/pages/Showcase";
import Booking from "@/pages/Booking";
import Socials from "@/pages/Socials";
import Contact from "@/pages/Contact";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import Photography from "@/pages/Photography";
import Videography from "@/pages/Videography";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PayPalReturn from "@/pages/PayPalReturn";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/beats" element={<Beats />} />
      <Route path="/catalog" element={<Catalog />} />
      <Route path="/showcase" element={<Showcase />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/socials" element={<Socials />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/account" element={<Account />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/photography" element={<Photography />} />
      <Route path="/videography" element={<Videography />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/payment/paypal-return" element={<PayPalReturn />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

function FreeUnlockModalWrapper() {
  const { freeBeat, closeFreeDownload } = usePlayer();
  return <FreeUnlockModal beat={freeBeat} onClose={closeFreeDownload} />;
}

function Shell() {
  const { current } = usePlayer();
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <PromoBanner />
      <main className={current ? "pb-28" : ""}>
        <AppRouter />
      </main>
      <Footer />
      <MiniPlayer />
      <LicenseModal />
      <FreeUnlockModalWrapper />
      <ChatWidget />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="noise min-h-screen bg-[#0B0B0E]">
      <BrowserRouter>
        <AuthProvider>
          <PlayerProvider>
            <Shell />
            <Toaster theme="dark" position="top-center" richColors />
          </PlayerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
